import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClientAuth } from '@contexts/ClientAuthContext';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import TurnstileWidget, { TurnstileWidgetHandle } from '@components/ui/TurnstileWidget';
import bbhLogoUrl from '@/assets/bbh-logo.svg';
import './ClientLogin.css';

const ClientLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { person, loading, login } = useClientAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );
  const [submitting, setSubmitting] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSubmitted, setForgotSubmitted] = useState(false);

  const siteKey = import.meta.env.PUBLIC_CLIENT_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!loading && person) {
      navigate('/client', { replace: true });
    }
  }, [person, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim() || !password) {
      setError('Please enter your username and password.');
      return;
    }

    setInfoMessage(null);
    setSubmitting(true);
    const result = await login(username.trim(), password, turnstileToken);
    setSubmitting(false);

    if (result.success) {
      navigate('/client', { replace: true });
    } else {
      setError(result.error ?? 'Sign in failed. Please try again.');
      setTurnstileToken('');
      turnstileRef.current?.reset();
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotSubmitting(true);
    try {
      await fetch('/api/client/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername.trim() }),
      });
    } catch {
      // Ignore -- the confirmation message is the same regardless.
    } finally {
      setForgotSubmitting(false);
      setForgotSubmitted(true);
    }
  };

  const backToSignIn = () => {
    setMode('login');
    setForgotUsername('');
    setForgotSubmitted(false);
  };

  if (loading) {
    return null;
  }

  return (
    <div className="client-login-page">
      <div className="client-login-card">
        <div className="client-login-header">
          <img className="client-login-logo" src={bbhLogoUrl} alt="" />
          <h1 className="client-login-title">Client Portal</h1>
          <p className="client-login-subtitle">
            {mode === 'login' ? 'Sign in to view and manage your project outcomes' : 'Reset your password'}
          </p>
        </div>

        {mode === 'forgot' ? (
          forgotSubmitted ? (
            <>
              <p className="client-login-info">
                If that account exists, we've sent an email with a link to set a new password.
              </p>
              <Button type="button" variant="secondary" size="lg" className="client-login-submit" onClick={backToSignIn}>
                Back to sign in
              </Button>
            </>
          ) : (
            <form className="client-login-form" onSubmit={handleForgotSubmit} noValidate>
              <div className="client-login-field">
                <label htmlFor="forgot-username" className="client-login-label">
                  Username
                </label>
                <Input
                  id="forgot-username"
                  type="text"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  disabled={forgotSubmitting}
                />
              </div>

              <Button type="submit" variant="primary" size="lg" disabled={forgotSubmitting} className="client-login-submit">
                {forgotSubmitting ? 'Sending…' : 'Send reset link'}
              </Button>
              <Button type="button" variant="secondary" size="lg" className="client-login-submit" onClick={backToSignIn}>
                Back to sign in
              </Button>
            </form>
          )
        ) : (
          <form className="client-login-form" onSubmit={handleSubmit} noValidate>
            {infoMessage && !error && <div className="client-login-info">{infoMessage}</div>}
            {error && (
              <div className="client-login-error" role="alert">
                {error}
              </div>
            )}

            <div className="client-login-field">
              <label htmlFor="username" className="client-login-label">
                Username
              </label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                disabled={submitting}
              />
            </div>

            <div className="client-login-field">
              <label htmlFor="password" className="client-login-label">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={submitting}
              />
            </div>

            {siteKey && (
              <div className="client-login-turnstile">
                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={siteKey}
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                />
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={submitting}
              className="client-login-submit"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <button
              type="button"
              className="client-login-forgot-link"
              onClick={() => setMode('forgot')}
            >
              Forgot password?
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClientLogin;
